/**
 * Change Management - Tracks form field changes and warns users before leaving.
 * Prevents accidental data loss by prompting users when navigating away from modified forms.
 */

// Declare external dependencies

// Interface for form element with change management
interface CMFormElement {
  name: string;
  changeManagement?: boolean;
  formObj: CMFormObj;
  get: () => string;
  set: (value: string | boolean) => void;
  hasChanged: (flag: boolean) => boolean;
}

// Interface for form object
interface CMFormObj {
  changeManagement?: boolean;
  getElement: (name: string) => CMFormElement | null;
}

// Interface for link element with extended properties
interface CMLink extends HTMLAnchorElement {
  oldOnClick?: (() => boolean) | null;
}

/**
 * ChangeManager tracks field changes and manages navigation warnings
 */
class ChangeManager {
  fieldValueChanged: boolean | null;
  allowLeaveForm: boolean;
  isOn: boolean;
  suspended: Record<string, boolean>;
  eventsCaptured: boolean;

  constructor() {
    this.fieldValueChanged = false;
    this.allowLeaveForm = false;
    this.isOn = true;
    this.suspended = {};
    this.eventsCaptured = false;
  }

  /**
   * Called when a field value changes
   */
  fieldChanged(e: CMFormElement): void {
    if (this.isOn && e.changeManagement !== false && e.formObj.changeManagement === true && this.suspended[e.name] !== true) {
      this.fieldValueChanged = true;
    }
  }

  /**
   * Called when a field loses focus
   */
  fieldBlur(e: CMFormElement): void {
    if (this.isOn && e.changeManagement !== false && e.formObj.changeManagement === true && this.suspended[e.name] !== true) {
      if (this.fieldValueChanged == null) {
        this.fieldValueChanged = e.hasChanged(false);
      }
    }
  }

  /**
   * Called on key press - marks potential change
   */
  keyPress(): void {
    this.fieldValueChanged = this.fieldValueChanged ? true : null;
  }

  /**
   * Suspends change tracking for an element or globally
   */
  suspend(e?: CMFormElement): void {
    if (e == null) {
      this.isOn = false;
    } else {
      this.suspended[e.name] = true;
    }
  }

  /**
   * Resumes change tracking for an element or globally
   */
  resume(e?: CMFormElement): void {
    if (e == null) {
      this.isOn = true;
    } else {
      this.suspended[e.name] = false;
    }
  }

  /**
   * Registers a form for change management
   */
  registerForm(formObj: CMFormObj): void {
    if (!this.eventsCaptured) {
      this.eventsCaptured = true;
      setTimeout(() => { _CM_.captureUnloadEvents(); }, 100);
    }
    const e = formObj.getElement("_fieldChanged");
    if (e != null) {
      this.fieldValueChanged = e.get() === "true";
    }
  }

  /**
   * Records current change state to form hidden field
   */
  record(formObj: CMFormObj): void {
    const e = formObj.getElement("_fieldChanged");
    if (e != null) {
      e.set(this.fieldValueChanged as boolean);
    }
  }

  /**
   * Allows leaving the form without warning
   */
  allowLeave(): void {
    this.allowLeaveForm = true;
  }

  /**
   * Captures unload events on all links
   */
  captureUnloadEvents(): void {
    const links = document.links;
    for (let i = 0; i < links.length; i++) {
      const link = links[i] as CMLink;
      const href = link.href;
      const lClass = link.className;

      if (href != null) {
        const hrefLower = href.toLowerCase();
        if (hrefLower.indexOf("mailto:") !== 0) {
          if (link.onclick != null && link.onclick !== fixJSLink) {
            link.oldOnClick = link.onclick as (() => boolean);
          }
          if (hrefLower.indexOf("javascript:") === -1) {
            if (lClass.indexOf("navParent") !== -1 || lClass.indexOf("ignoreChangeCheck") !== -1) {
              // Skip these links
            } else {
              link.onclick = hrefLower.indexOf("/download/") !== -1 ? acceptLink : fieldValueChangedCheck;
            }
          } else if (hrefLower.indexOf("javascript:%20history.") === 0) {
            link.onclick = acceptLink;
          } else {
            link.onclick = fixJSLink;
          }
        }
      }
    }
    (document.body as any).onbeforeunload = onUnloadCheck;
  }
}

// Global instance
const _CM_ = new ChangeManager();

/**
 * Called before page unload - warns if changes exist
 */
function onUnloadCheck(): string | undefined {
  if (_CM_.fieldValueChanged !== false && !_CM_.allowLeaveForm) {
    return "You have changed data.  If you continue, your changes will be lost!";
  }
  return undefined;
}

/**
 * Accepts a link click without warning
 */
function acceptLink(this: CMLink): boolean | void {
  if (this.oldOnClick != null && this.oldOnClick() === false) {
    return false;
  }
  _CM_.allowLeave();
  setTimeout(function() { _CM_.allowLeaveForm = false; }, 3000);
}

/**
 * Fixes JavaScript links by evaluating their href
 */
function fixJSLink(this: CMLink): boolean {
  if (this.oldOnClick != null && this.oldOnClick() === false) {
    return false;
  }
  // Use decodeURIComponent instead of deprecated unescape
  // Note: eval is unavoidable here - we must execute arbitrary JS from javascript: hrefs
  // This is safe because the JS code comes from the page's own HTML, not user input
  let href = decodeURIComponent(this.href);
  const pos = href.toLowerCase().indexOf("javascript:");
  if (pos === -1) return true;
  href = href.substring(pos + 11);
  eval(href);
  return false;
}

/**
 * Checks for field value changes before navigation
 */
function fieldValueChangedCheck(this: CMLink): boolean | void {
  if (this.oldOnClick != null && this.oldOnClick() === false) {
    return false;
  }
  if (_CM_.fieldValueChanged !== false && !_CM_.allowLeaveForm) {
    let result: number;
    if ((document as any).all && typeof uberPrompt === "function") {
      result = uberPrompt(
        '<p align="center" style="color:red;font-size:14pt">You have made changes.</p><p>Click "Save" to save and continue.<br>Click "Don\'t Save" to continue without saving.<br>Click "Cancel" to remain on the form.',
        ["Save", "Don't Save", "Cancel"],
        300, 200, "Yes", "Cancel"
      );
    } else {
      result = confirm("You have changed data.  Click 'Ok' to save and continue or 'Cancel' to continue without saving.") ? 0 : 1;
    }
    if (result === 0) {
      submitForm(null, null, this.href);
    }
    if (result !== 1) {
      return false;
    }
    _CM_.allowLeave();
  }
}

// Export for module usage
export {
  ChangeManager,
  _CM_,
  onUnloadCheck,
  acceptLink,
  fixJSLink,
  fieldValueChangedCheck
};

// Export types
export type { CMFormElement, CMFormObj, CMLink };

// Expose on globalThis for backwards compatibility
(globalThis as any).ChangeManager = ChangeManager;
(globalThis as any)._CM_ = _CM_;
(globalThis as any).onUnloadCheck = onUnloadCheck;
(globalThis as any).acceptLink = acceptLink;
(globalThis as any).fixJSLink = fixJSLink;
(globalThis as any).fieldValueChangedCheck = fieldValueChangedCheck;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("changemanagement.js");
}
